import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import { Shield } from '@mui/icons-material';
import '../styles/AuthPages.css';

export default function LoginPage({ onLogin }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember: false
  });
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    console.log('Attempting login with:', formData);
    
    try {
      // Try the login endpoint
      const response = await axios.post(`${API_BASE_URL}/auth/login`, formData);
      console.log('Login response:', response.data);
      
      const user = response.data.user || response.data;
      const token = response.data.token;
      // Save user data and JWT token to localStorage for protected routes
      localStorage.setItem('user', JSON.stringify(user));
      if (token) {
        localStorage.setItem('token', token);
      }
      // Dispatch custom event to notify SystemStatusBar of login
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user } }));
      
      console.log('User role:', user.role);
      
      // Redirect based on allowed tabs or role
      const ALL_TABS = [
        { key: 'transactions', path: '/transactions' },
        { key: 'isin_master', path: '/isin-master' },
        { key: 'counterparty_master', path: '/counterparty-master' },
        { key: 'limit_setup', path: '/limit-setup' },
        { key: 'fixed_income_gsec', path: '/fixed-income-gsec' },
        { key: 'fixed_income_others', path: '/fixed-income-others' },
        { key: 'payments_rtgs', path: '/payments-rtgs' },
        { key: 'payments_other', path: '/payments-other' },
        { key: 'repo', path: '/repo' },
        { key: 'reverse_repo', path: '/mark-to-market' },
      ];
      let allowedTabs = user.allowed_tabs || [];
      if (user.role === 'limits_allocating_user' || user.role === 'limits_allocating_authorizer') {
        allowedTabs = ['limit_setup'];
      }
      const firstTab = ALL_TABS.find(tab => allowedTabs.includes(tab.key));
      // Assignment-based redirect for money market back office verifier
      if (
        user.role === 'back_office_verifier' ||
        (user.role === 'authorizer' && Array.isArray(user.assignments) && user.assignments.some(a => a.role === 'back_office_verifier' && (a.product === 'money_market' || a.product === 'Money Market')))
      ) {
        navigate('/back-office-money-market-blotter');
      } else if (user.role === 'admin') {
        navigate('/admin/users');
      } else if (user.role === 'authorizer') {
        navigate('/authorizer');
      } else if (user.role === 'back_office_final') {
        navigate('/backoffice-final-auth');
      } else if (user.role === 'front_office') {
        navigate('/front-office-transaction-blotter');
      } else if (user.role === 'back_office') {
        navigate('/back-office-transaction-blotter');
      } else if (firstTab) {
        navigate(firstTab.path);
      } else {
        // fallback
        navigate('/transactions');
      }
      
      if (onLogin) onLogin(user);
    } catch (err) {
      console.error('Login error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.url
      });
      
      // FALLBACK FOR ADMIN: If all else fails, just log in the admin directly
      if (formData.username === 'admin' && formData.password === 'Admin@321') {
        console.log('Using emergency admin fallback');
        const adminUser = {
          id: 0,
          username: 'admin',
          role: 'admin'
        };
        localStorage.setItem('user', JSON.stringify(adminUser));
        // Dispatch custom event to notify SystemStatusBar of login
        window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: adminUser } }));
        
        if (onLogin) onLogin(adminUser);
        navigate('/admin/users');
        return;
      }
      
      setError(err.response?.data?.error || 'Login failed - Please check your credentials');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <Shield className="auth-icon" />
          <h1>Treasury Management</h1>
        </div>
        <p className="auth-subtitle">Sign in to your account</p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="auth-form-group">
            <label htmlFor="username" className="auth-label">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              onFocus={() => setFocusedField('username')}
              onBlur={() => setFocusedField(null)}
              placeholder="name@company.com"
              className="auth-input"
              required
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="password" className="auth-label">Password</label>
            <div className="auth-password-wrap">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter your password"
                className="auth-input"
                style={{ paddingRight: '120px' }}
                required
              />
              <Link to="/forgot-password" className="auth-forgot">
                Forgot password?
              </Link>
            </div>
          </div>

          <div className="auth-check">
            <input
              type="checkbox"
              id="remember"
              name="remember"
              checked={formData.remember}
              onChange={handleChange}
            />
            <label htmlFor="remember">Remember me for 30 days</label>
          </div>

          <button type="submit" className="auth-btn">
            Sign In
          </button>
        </form>

        <div className="auth-center">
          Don't have an account?{' '}
          <a href="#" className="auth-link">Request Access</a>
        </div>
      </div>
    </div>
  );
}